import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { linkSignals } from "../api/abort";
import { apiRequest, pagedOrThrow, pageQuery, type Envelope, type PagedData } from "../api/client";
import type { IFilter, Player, Session } from "../api/models";
import { isTokenValid, readClaims } from "../auth/token";
import { environment } from "../env";
import { latestSession, type GreetRuleMatch, type QueuedNotification, type TimeFrame, type TripSort } from "./profile-logic";

// THE PLAYER PROFILE'S ENDPOINTS (`players.service.ts`, `player-communications.service.ts`,
// `events-manager.service.ts`, `task.service.ts`). PATHS, QUERY STRINGS AND BODIES ARE
// ANGULAR'S, VERBATIM; DATES SERIALIZE AS ISO UTC STRINGS, AS ANGULAR'S JSON DOES.

const user = () => String(readClaims()?.userId ?? "anonymous");
export const key = (...parts: unknown[]) => [user(), "profile", ...parts];
// THE ONE-SHOT PROFILE-ENTRY READS LIVE OUTSIDE "profile", SO REFRESHING THE PROFILE AFTER A SAVE
// NEVER RE-FETCHES THEM (ANGULAR LOADS THEM ONCE PER VISIT)
export const entryKey = (...parts: unknown[]) => [user(), "profile-entry", ...parts];
// EVERY PROFILE READ, SO ONE WRITE CAN REFRESH THEM ALL (ANGULAR RE-FETCHES PIECEMEAL)
export const invalidateProfile = (qc: QueryClient) => Promise.all([qc.invalidateQueries({ queryKey: key() }), qc.invalidateQueries({ queryKey: [user(), "player"] })]);

const post = <T>(path: string, body: unknown, signal?: AbortSignal) => apiRequest<Envelope<T>>(path, { method: "POST", body, signal }).then((r) => r.data);

// IN PLAY ------------------------------------------------------------------------

const ACTIVE_SESSION_POLL_MS = 15_000;
const ACTIVE_SESSION_TIMEOUT_MS = 10_000;
// CONSECUTIVE FAILED TICKS AFTER WHICH POLLING STOPS (THE "UNAVAILABLE" LINE STAYS)
const consecutiveFailures = new Map<string, number>();

// THE OPEN SESSION (`end_time IS NULL`) FOR THIS PLAYER, ACROSS EVERY PROPERTY (`site_ids=`
// EMPTY). IT MATCHES ON `players.cms_player_id`: `sessions.cms_player_id` IS ALWAYS NULL, SO
// FILTERING ON IT SILENTLY RETURNS NOTHING. POLLED EVERY 15S; EACH TICK TRIES THREE TIMES
// WITHIN 10S, AND THREE FAILED TICKS IN A ROW STOP THE POLL.
export function useActiveSession(cmsPlayerId: string | undefined) {
  const k = cmsPlayerId ?? "";
  return useQuery({
    queryKey: key("active-session", k),
    enabled: !!cmsPlayerId,
    retry: false,
    queryFn: async ({ signal }) => {
      // SIGNED OUT MID-POLL: UNKNOWN, NOT "NOT IN PLAY"
      if (!isTokenValid()) throw new Error("Not signed in");
      // THE TICK'S OWN 10S CEILING, LINKED TO THE QUERY'S SIGNAL (NOT `AbortSignal.any`: SEE `linkSignals`)
      const linked = linkSignals([signal], ACTIVE_SESSION_TIMEOUT_MS);
      const tick = linked.signal;
      try {
        const filters: IFilter[] = [
          { field: "end_time", value: "", valueDataType: "datetime", type: "IS", condition: "AND" },
          { field: "players.cms_player_id", value: k, valueDataType: "string", type: "=", condition: "AND" },
        ];
        let lastError: unknown;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const data = await post<PagedData<Session>>(
              `/ht/paged-sessions?${pageQuery({ page: 1, pageSize: 25, orderBy: "start_time", orderDir: "desc" }, { site_ids: "" })}`,
              filters,
              tick,
            );
            consecutiveFailures.set(k, 0);
            return latestSession(data?.rows);
          } catch (e) {
            lastError = e;
            if (tick.aborted) break;
          }
        }
        consecutiveFailures.set(k, (consecutiveFailures.get(k) ?? 0) + 1);
        throw lastError;
      } finally {
        linked.dispose();
      }
    },
    refetchInterval: () => ((consecutiveFailures.get(k) ?? 0) >= 3 ? false : ACTIVE_SESSION_POLL_MS),
    refetchIntervalInBackground: false,
  });
}

// TRIPS AND TOTALS ------------------------------------------------------------------

export interface Trip {
  id: number;
  playerId?: string;
  player_id?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  sessionCount?: number;
  totalDailyActual?: number;
  totalDailyTheo?: number;
}

export interface TripsResponse {
  pagedTrips: PagedData<Trip>;
  averageDailyActual?: number;
  averageDailyTheo?: number;
  totalActual?: number;
  totalTheo?: number;
  tripCount?: number;
}

export const TRIPS_PAGE_SIZE = 20;

// `getPagedPlayerTrips`: KEYED BY THE VERIFIED CARD ID ONLY (ISSUE #78) - NEVER THE ROUTE
// PARAM WHEN THE NAVIGATION NAMED A PARTICULAR PLAYER
export function useTrips(cardId: string | null, page: number, sort: TripSort, dir: "asc" | "desc", tf: TimeFrame) {
  return useQuery({
    queryKey: key("trips", cardId, page, sort, dir, tf.start_time.toISOString(), tf.end_time.toISOString()),
    enabled: !!cardId,
    queryFn: async ({ signal }) => {
      const data = await post<TripsResponse>(
        `/player/paged-trips?${pageQuery({ page, pageSize: TRIPS_PAGE_SIZE, orderBy: sort, orderDir: dir })}`,
        { playerId: cardId, timeFrame: { start_time: tf.start_time, end_time: tf.end_time } },
        signal,
      );
      if (!data) throw new Error("paged-trips returned no data");
      pagedOrThrow(data.pagedTrips, "paged-trips");
      return data;
    },
    // A FAILED PAGE KEEPS THE ROWS ALREADY ON SCREEN, AS ANGULAR DOES
    placeholderData: (previous) => previous,
  });
}

export interface TripSessionRow {
  id?: number;
  siteName?: string;
  description?: string;
  location?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  gains?: number;
  theoWin?: number;
}

export function useTripSessions(cardId: string, tripId: number) {
  return useQuery({
    queryKey: key("trip-sessions", cardId, tripId),
    queryFn: ({ signal }) => post<TripSessionRow[] | null>("/player/trip-sessions", { playerId: cardId, tripId }, signal).then((d) => d ?? []),
  });
}

// REINVESTMENT (THE INTERNAL `player.id`, NOT THE CARD) ----------------------------------

export function useReinvestment(playerInternalId: number | undefined, tf: TimeFrame) {
  return useQuery({
    queryKey: key("reinvestment", playerInternalId, tf.start_time.toISOString(), tf.end_time.toISOString()),
    enabled: !!playerInternalId,
    queryFn: ({ signal }) =>
      post<{ lifetimeReinvestmentTwinRatio?: number } | null>(
        `/communications/players/${playerInternalId}/reinvestment`,
        { start_time: tf.start_time, end_time: tf.end_time },
        signal,
      ).then((d) => d?.lifetimeReinvestmentTwinRatio ?? null),
  });
}

// ACTIVITIES ------------------------------------------------------------------------

export interface Activity {
  completed?: boolean;
  type?: string;
  subtype?: string;
  completedDate?: string;
  hostName?: string;
  description?: string;
}

export function useActivities(player: Player | undefined, page: number) {
  return useQuery({
    queryKey: key("activities", player?.id, page),
    enabled: !!player?.playerId,
    queryFn: ({ signal }) =>
      post<PagedData<Activity>>(
        `/crm/paged-host-player-activity?${pageQuery({ page, pageSize: 15, orderBy: "completedDate", orderDir: "desc" })}`,
        { id: player!.id, playerId: player!.playerId },
        signal,
      ).then((d) => pagedOrThrow(d, "paged-host-player-activity")),
    placeholderData: (previous) => previous,
  });
}

// GREETS ----------------------------------------------------------------------------

export interface Greet {
  id?: number;
  status?: string;
  created_at?: string;
  hostName?: string;
  egm_floor_location?: string | null;
  message?: string;
}

const byCms = (cms: string): IFilter[] => [{ field: "cms_player_id", value: cms, valueDataType: "string", type: "=", condition: "AND" }];

export function useGreets(cmsPlayerId: string | undefined, page: number, enabled: boolean) {
  return useQuery({
    queryKey: key("greets", cmsPlayerId, page),
    enabled: enabled && !!cmsPlayerId,
    queryFn: ({ signal }) =>
      post<PagedData<Greet>>(
        `/player/paged-player-greets?${pageQuery({ page, pageSize: 5, orderBy: "created_at", orderDir: "desc" })}`,
        byCms(cmsPlayerId!),
        signal,
      ).then((d) => pagedOrThrow(d, "paged-player-greets")),
    placeholderData: (previous) => previous,
  });
}

export function useCreateGreet(cmsPlayerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (g: { status: string; message: string }) => post("/player/create-player-greet", { status: g.status, message: g.message, cms_player_id: cmsPlayerId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key("greets", cmsPlayerId) }),
  });
}

// TASKS -----------------------------------------------------------------------------

export interface PlayerTask {
  playerId: number;
  taskId: number;
  taskActionId?: number | null;
  taskTitle?: string;
  taskStartTime?: string;
  taskEndTime?: string;
  hostName?: string;
  completed?: boolean;
  taskOutcomeId?: number | null;
  taskOutcomeNote?: string | null;
}

// THE SERVER RETURNS ONLY THE PLAYER'S INCOMPLETE TASKS
export function usePlayerTasks(player: Player | undefined, enabled: boolean) {
  return useQuery({
    queryKey: key("player-tasks", player?.id),
    enabled: enabled && !!player?.id,
    queryFn: ({ signal }) => post<PlayerTask[] | null>("/crm/get-player-tasks", { id: player!.id, playerId: player!.playerId }, signal).then((d) => d ?? []),
  });
}

// THE "CREATE CONTACT" RECORD: A COMPLETED, 15-MINUTE "PLAYER CONTACT" TASK FOR THIS PLAYER
export function contactTaskBody(input: { start: Date; taskActionId: number; taskOutcomeId: number; taskOutcomeNote: string; playerInternalId: number }) {
  const end = new Date(input.start.getTime() + 15 * 60_000);
  return {
    id: null,
    startTime: input.start,
    endTime: end,
    title: "Player Contact",
    description: "",
    managerUserId: null,
    completed: true,
    alertTime: null,
    alertOption: "",
    expirationDate: end,
    parentTaskId: null,
    taskActionId: input.taskActionId,
    players: [{ playerId: input.playerInternalId, taskId: null, completed: true, taskOutcomeId: input.taskOutcomeId, taskOutcomeNote: input.taskOutcomeNote }],
  };
}

// NOTES -----------------------------------------------------------------------------

export function useDeleteNote(cmsPlayerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    // ANGULAR POSTS THE WHOLE NOTE ROW
    mutationFn: (note: unknown) => post("/player/delete-player-note", note),
    onSuccess: () => qc.invalidateQueries({ queryKey: [user(), "notes", cmsPlayerId ?? ""] }),
  });
}

// PREFERENCES -----------------------------------------------------------------------

export interface PreferenceCategory {
  id: number;
  code?: string;
  name: string;
}

export interface Preference {
  id: number;
  patronId?: number;
  patronPreferenceCategoryId?: number;
  patronPreferenceCategoryCode?: string;
  patronPreferenceCategoryName?: string;
  value?: string;
  priority?: number;
  actionable?: boolean;
  [serverField: string]: unknown;
}

export function usePreferenceCategories() {
  return useQuery({
    queryKey: key("preference-categories"),
    staleTime: Infinity,
    queryFn: ({ signal }) =>
      apiRequest<Envelope<PreferenceCategory[] | null>>("/patron/preference/get-patron-preference-categories", { signal }).then((r) =>
        [...(r.data ?? [])].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
      ),
  });
}

// SORTED BY PRIORITY; A PLAYER WITH NO `patronId` HAS NONE (AND NO REQUEST IS MADE)
export function usePreferences(patronId: number | undefined) {
  return useQuery({
    queryKey: key("preferences", patronId),
    enabled: !!patronId,
    queryFn: ({ signal }) =>
      post<Preference[] | null>("/patron/preference/get-patron-preferences", { patronId }, signal).then((d) =>
        [...(d ?? [])].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
      ),
  });
}

function usePreferenceWrite<T>(path: string, patronId: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: T) => post(path, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: key("preferences", patronId) }),
  });
}

export const useCreatePreference = (patronId: number | undefined) => usePreferenceWrite<Preference>("/patron/preference/create-patron-preference", patronId);
export const useUpdatePreference = (patronId: number | undefined) => usePreferenceWrite<Preference>("/patron/preference/update-patron-preference", patronId);
export const useDeletePreference = (patronId: number | undefined) => usePreferenceWrite<Preference>("/patron/preference/delete-patron-preference", patronId);
export const useCreatePreferences = (patronId: number | undefined) => usePreferenceWrite<Preference[]>("/patron/preference/create-patron-preferences", patronId);

// AI PREFERENCE GENERATION, THROUGH THE CORE API (NEVER A PROVIDER DIRECTLY)
export const generatePreferences = (preferencesText: string) =>
  post<Preference[] | null>("/patron/preference/generate-patron-preferences", { preferencesText }).then((d) => d ?? []);

// EVENTS ----------------------------------------------------------------------------

export interface PlayerEvent {
  eventId: number;
  eventName?: string;
  eventStatus?: string;
  startDateTime?: string;
  endDateTime?: string;
  eventCurrentBookings?: number;
  eventTotalCapacity?: number;
  isBooked?: boolean;
  eventBookingId?: number | null;
  eventBookingStatus?: string | null;
  eventBookingCancelledAt?: string | null;
  numberOfTickets?: number;
  eventCanAcceptBookings?: boolean;
}

export const fetchPlayerEvents = (playerInternalId: number) =>
  apiRequest<Envelope<PlayerEvent[] | null>>(`/events/player-events/${playerInternalId}`).then((r) =>
    [...(r.data ?? [])].sort((a, b) => new Date(b.startDateTime ?? 0).getTime() - new Date(a.startDateTime ?? 0).getTime()),
  );

export const bookEvent = (body: {
  eventId: number;
  playerId: number;
  playerName: string;
  playerCardNumber: string;
  hostId: number;
  numberOfTickets: number;
  notes: string;
  status: "CONFIRMED";
}) => post(`/events/${body.eventId}/bookings`, body);

export const setBookingStatus = (eventId: number, bookingId: number, status: "CONFIRMED" | "DECLINED") =>
  apiRequest<Envelope<unknown>>(`/events/${eventId}/bookings/${bookingId}`, { method: "PUT", body: { status } });

// OVERRIDE REQUESTS -----------------------------------------------------------------

export interface OpenEvent {
  id: number;
  name: string;
  startDateTime?: string;
}

export const fetchOpenEvents = () => apiRequest<Envelope<OpenEvent[] | null>>("/events?status=BOOKING").then((r) => r.data ?? []);

export interface CommsPlayer {
  playerId?: number;
  firstName?: string;
  lastName?: string;
  cardId?: string;
}

// THIS ENDPOINT ANSWERS WITH THE PAGE ITSELF (`{ rows, ... }`), NOT A `{ data }` ENVELOPE -
// ANGULAR READS `results.rows` STRAIGHT OFF THE BODY
export const searchCommsPlayers = (filter: string, signal?: AbortSignal) =>
  apiRequest<{ rows?: CommsPlayer[]; data?: { rows?: CommsPlayer[] } } | null>(
    `/communications/players/search?${pageQuery({ page: 1, pageSize: 10, orderBy: "name", orderDir: "asc" })}`,
    { method: "POST", body: { filter, page: 1, page_size: 10, order_by: "name", order_dir: "asc" }, signal },
  ).then((d) => d?.rows ?? d?.data?.rows ?? []);

// `formatPlayerName`: FIRST AND LAST, TOLERATING EITHER MISSING
export const commsPlayerName = (p: CommsPlayer) => `${(p.firstName ?? "").trim()} ${(p.lastName ?? "").trim()}`.trim();

export const submitOverride = (body: {
  eventId: number;
  playerId: number;
  playerCardNumber: string;
  playerName: string;
  userId: number;
  numberOfTickets: number;
  justification: string;
  status: "PENDING";
}) => post("/events/overrides", body);

// PROFILE-ENTRY ITEMS ---------------------------------------------------------------

export const fetchProfileNotifications = (cmsPlayerId: string, signal?: AbortSignal) =>
  apiRequest<Envelope<QueuedNotification[] | null>>(`/player/${encodeURIComponent(cmsPlayerId)}/profile-notifications`, { signal }).then((r) => r.data ?? []);

// FIRE-AND-FORGET: A FAILURE ONLY MEANS THE ITEMS RESURFACE NEXT VISIT
export const markProfileNotificationsViewed = (cmsPlayerId: string, ids: number[]) =>
  post(`/player/${encodeURIComponent(cmsPlayerId)}/profile-notifications/viewed`, { ids }).catch(() => undefined);

export const evaluateGreetRules = (cmsPlayerId: string, signal?: AbortSignal) =>
  post<GreetRuleMatch[] | null>("/crm/notification/evaluate-player-greet-rules", { cmsPlayerId, appCode: environment.application }, signal).then((d) => d ?? []);

// THE SIGNED-IN USER (A BOOKING'S `hostId`, AN OVERRIDE'S `userId`)
export const currentUserId = () => Number(readClaims()?.userId ?? 0);
